import React, { useEffect, useMemo } from 'react';
import { Archive, Package, ShoppingCart, Sparkles, Wrench } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useInventoryStore } from '../../../store/inventoryStore';
import { useWidgetSize } from './useWidgetSize';

interface StockByItemWidgetProps {
  title: string;
  accentColor: string;
  bgTint: string;
  isDark: boolean;
  catalogueItemId: string | null;
  scopeWarehouseId: string | null;
  scopeStorageAreaId: string | null;
  variant?: 'detail' | 'condition_counts';
}

type StatKey = 'total' | 'new' | 'used' | 'out' | 'retired';

interface StatMeta {
  label: string;
  Icon: LucideIcon;
  tile: { dark: string; light: string };
  value: { dark: string; light: string };
  icon: { dark: string; light: string };
}

const STAT_META: Record<StatKey, StatMeta> = {
  total: {
    label: 'On hand',
    Icon: Package,
    tile: {
      dark: 'bg-sky-950/40 border-sky-900/45',
      light: 'bg-sky-50 border-sky-100',
    },
    value: { dark: '#7dd3fc', light: '#0369a1' },
    icon: { dark: '#38bdf8', light: '#0284c7' },
  },
  new: {
    label: 'New',
    Icon: Sparkles,
    tile: {
      dark: 'bg-blue-950/35 border-blue-900/45',
      light: 'bg-blue-50 border-blue-100',
    },
    value: { dark: '#93c5fd', light: '#1d4ed8' },
    icon: { dark: '#60a5fa', light: '#2563eb' },
  },
  used: {
    label: 'Used',
    Icon: Wrench,
    tile: {
      dark: 'bg-[#2b3140]/80 border-[#3a404a]',
      light: 'bg-gray-50 border-gray-200',
    },
    value: { dark: '#e5e7eb', light: '#374151' },
    icon: { dark: '#9ca3af', light: '#6b7280' },
  },
  out: {
    label: 'Out',
    Icon: ShoppingCart,
    tile: {
      dark: 'bg-amber-950/35 border-amber-900/45',
      light: 'bg-amber-50 border-amber-100',
    },
    value: { dark: '#fbbf24', light: '#b45309' },
    icon: { dark: '#f59e0b', light: '#d97706' },
  },
  retired: {
    label: 'Retired',
    Icon: Archive,
    tile: {
      dark: 'bg-red-950/30 border-red-900/45',
      light: 'bg-red-50 border-red-100',
    },
    value: { dark: '#fca5a5', light: '#991b1b' },
    icon: { dark: '#f87171', light: '#dc2626' },
  },
};

const StatTile: React.FC<{
  statKey: StatKey;
  value: number;
  isDark: boolean;
  accentColor: string;
  isXS: boolean;
  isSM: boolean;
  featured?: boolean;
}> = ({ statKey, value, isDark, accentColor, isXS, isSM, featured = false }) => {
  const meta = STAT_META[statKey];
  const Icon = meta.Icon;

  const labelCls = isXS ? 'text-[9px]' : isSM ? 'text-[10px]' : 'text-xs';
  const valueCls = featured
    ? (isXS ? 'text-2xl' : isSM ? 'text-3xl' : 'text-4xl')
    : (isXS ? 'text-lg' : isSM ? 'text-xl' : 'text-2xl');
  const valueWeightCls = featured ? 'font-bold' : 'font-semibold';
  const iconSize = featured ? (isXS ? 11 : 12) : isXS ? 10 : 11;
  const padCls = isXS ? 'px-2 py-1.5' : 'px-2.5 py-2';

  const useAccent = statKey === 'total' && !!accentColor;
  const valueColor = useAccent
    ? accentColor
    : isDark
      ? meta.value.dark
      : meta.value.light;
  const iconColor = useAccent
    ? accentColor
    : isDark
      ? meta.icon.dark
      : meta.icon.light;

  const tileCls = isDark ? meta.tile.dark : meta.tile.light;

  return (
    <div
      className={`flex h-full min-h-0 min-w-0 flex-col justify-between overflow-hidden rounded border box-border ${padCls} ${tileCls}`}
    >
      <div className="flex items-center gap-1 min-w-0 flex-shrink-0">
        <Icon size={iconSize} className="flex-shrink-0" style={{ color: iconColor }} />
        <span
          className={`${labelCls} truncate leading-tight ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
        >
          {meta.label}
        </span>
      </div>
      <span
        className={`${valueCls} ${valueWeightCls} leading-tight flex-shrink-0`}
        style={{ color: valueColor }}
      >
        {value}
      </span>
    </div>
  );
};

const StockByItemWidget: React.FC<StockByItemWidgetProps> = ({
  title,
  accentColor,
  bgTint,
  isDark,
  catalogueItemId,
  scopeWarehouseId,
  scopeStorageAreaId,
  variant = 'detail',
}) => {
  const { ref, isXS, isSM } = useWidgetSize();
  const { items, stockUnits, fetchItems } = useInventoryStore();

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const stats = useMemo(() => {
    if (!catalogueItemId) return null;

    const item = items.find(i => i.id === catalogueItemId) ?? null;

    const scoped = stockUnits.filter(u => {
      if (scopeWarehouseId && u.warehouseId !== scopeWarehouseId) return false;
      if (scopeStorageAreaId && u.storageAreaId !== scopeStorageAreaId) return false;
      return true;
    });

    let newInStock = 0;
    let usedInStock = 0;
    let checkedOut = 0;
    let retired = 0;
    let onHand = 0;

    for (const u of scoped) {
      if (u.itemId !== catalogueItemId) continue;

      if (u.status === 'retired') {
        retired++;
        continue;
      }
      if (u.status === 'checked out') {
        checkedOut++;
        continue;
      }
      if (u.status === 'in stock') {
        if (u.condition === 'new') newInStock++;
        else if (u.condition === 'used') usedInStock++;
        else usedInStock++;
        onHand++;
      }
    }

    return { item, newInStock, usedInStock, checkedOut, retired, onHand };
  }, [items, stockUnits, catalogueItemId, scopeWarehouseId, scopeStorageAreaId]);

  /** Same tiers as CategoryWidget */
  const nameC  = isXS ? 'text-[10px]' : isSM ? 'text-xs' : 'text-sm';
  const subC   = isXS ? 'text-[9px]'  : 'text-xs';
  const padC   = isXS ? 'p-2' : 'p-3';
  const headC  = isXS ? 'text-xs' : isSM ? 'text-sm' : 'text-base';
  const gapC   = isXS ? 'gap-1.5' : 'gap-2';

  const border = isDark ? '#343a43' : '#e5e7eb';

  const statEntries: { key: StatKey; value: number }[] = stats
    ? [
        { key: 'new', value: stats.newInStock },
        { key: 'used', value: stats.usedInStock },
        { key: 'out', value: stats.checkedOut },
        { key: 'retired', value: stats.retired },
      ]
    : [];
  const conditionEntries: { key: StatKey; value: number }[] = stats
    ? [
        { key: 'new', value: stats.newInStock },
        { key: 'used', value: stats.usedInStock },
      ]
    : [];

  return (
    <div
      ref={ref}
      className={`h-full min-h-0 w-full flex flex-col ${padC} rounded border overflow-hidden`}
      style={{
        backgroundColor: bgTint || (isDark ? '#20242b' : '#ffffff'),
        borderColor: border,
      }}
    >
      <div className="flex-shrink-0 min-w-0 mb-2">
        <h3
          className={`${headC} font-semibold leading-snug truncate ${isDark ? 'text-gray-100' : 'text-gray-800'}`}
        >
          {title}
        </h3>
        {stats?.item && (
          <div className={`flex items-center gap-1.5 mt-1 min-w-0 ${nameC}`}>
            <span
              className={`truncate font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
              title={stats.item.name}
            >
              {stats.item.name}
            </span>
            {stats.item.isDiscontinued && (
              <span
                className={`flex-shrink-0 rounded px-1.5 py-0.5 ${subC} border ${
                  isDark
                    ? 'bg-[#451a03] text-orange-300 border-[#7c2d12]'
                    : 'bg-orange-50 text-orange-700 border-orange-200'
                }`}
              >
                Discontinued
              </span>
            )}
            {stats.item.sku && (
              <span
              className={`flex-shrink-0 rounded px-1.5 py-0.5 ${subC} border ${
                  isDark
                    ? 'bg-[#2b3140]/60 text-gray-400 border-[#3a404a]'
                    : 'bg-gray-100 text-gray-500 border-gray-200'
                }`}
              >
                {stats.item.sku}
              </span>
            )}
          </div>
        )}
      </div>

      {!catalogueItemId && (
        <p className={`text-xs leading-snug ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Choose an item type in widget settings.
        </p>
      )}

      {catalogueItemId && stats && !stats.item && (
        <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          Item no longer in catalogue.
        </p>
      )}

      {catalogueItemId && stats?.item && (
        variant === 'condition_counts' ? (
          <div className={`grid grid-cols-2 ${gapC} flex-1 min-h-0`}>
            {conditionEntries.map(({ key, value }) => (
              <div key={key} className="min-h-0 min-w-0">
                <StatTile
                  statKey={key}
                  value={value}
                  isDark={isDark}
                  accentColor={accentColor}
                  isXS={isXS}
                  isSM={isSM}
                  featured
                />
              </div>
            ))}
          </div>
        ) : (
          <div
            className={`grid grid-cols-3 grid-rows-2 ${gapC} flex-1 min-h-0`}
            style={{ gridTemplateRows: 'repeat(2, minmax(0, 1fr))' }}
          >
            <div className="row-span-2 min-h-0 min-w-0">
              <StatTile
                statKey="total"
                value={stats.onHand}
                isDark={isDark}
                accentColor={accentColor}
                isXS={isXS}
                isSM={isSM}
                featured
              />
            </div>
            {statEntries.map(({ key, value }) => (
              <div key={key} className="min-h-0 min-w-0">
                <StatTile
                  statKey={key}
                  value={value}
                  isDark={isDark}
                  accentColor={accentColor}
                  isXS={isXS}
                  isSM={isSM}
                />
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default StockByItemWidget;
